import React, { useState } from 'react';
import { X } from 'lucide-react';

interface PromotionGalleryProps {
  photos: string[];
}

const PromotionGallery: React.FC<PromotionGalleryProps> = ({ photos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  if (!photos || photos.length === 0) {
    return null;
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      {/* Carrousel */}
      <div className="bg-white dark:bg-gray-900 relative group">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden cursor-pointer" onClick={() => setShowLightbox(true)}>
            {/* Image principale */}
            <img
              src={photos[currentIndex]}
              alt={`Photo ${currentIndex + 1}`}
              className="w-full h-auto max-h-[500px] object-contain mx-auto transition-transform duration-300 hover:scale-[1.02]"
            />

            {/* Zones de navigation invisibles */}
            {photos.length > 1 && (
              <>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevious();
                  }}
                  className="absolute left-0 top-0 bottom-0 w-1/4 cursor-w-resize hover:bg-gradient-to-r hover:from-black/10 hover:to-transparent transition-all"
                />
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNext();
                  }}
                  className="absolute right-0 top-0 bottom-0 w-1/4 cursor-e-resize hover:bg-gradient-to-l hover:from-black/10 hover:to-transparent transition-all"
                />
              </>
            )}

            {/* Indicateurs discrets */}
            {photos.length > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 bg-black/20 backdrop-blur-sm px-4 py-2 rounded-full">
                {photos.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(index);
                    }}
                    className={`transition-all duration-300 rounded-full ${
                      index === currentIndex
                        ? 'bg-white w-8 h-2'
                        : 'bg-white/40 hover:bg-white/60 w-2 h-2'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Miniatures */}
          {photos.length > 1 && (
            <div className="px-4 py-3 flex space-x-2 overflow-x-auto">
              {photos.map((photo, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentIndex
                      ? 'border-blue-500 scale-105'
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={photo}
                    alt={`Miniature ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setShowLightbox(false)}
        >
          {/* Bouton fermer élégant */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowLightbox(false);
            }}
            className="absolute top-6 right-6 p-3 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-all duration-200 z-10"
          >
            <X className="w-7 h-7" />
          </button>

          {/* Image agrandie */}
          <img
            src={photos[currentIndex]}
            alt={`Photo ${currentIndex + 1}`}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Zones de navigation invisibles pour lightbox */}
          {photos.length > 1 && (
            <>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-0 top-20 bottom-20 w-1/3 cursor-w-resize hover:bg-gradient-to-r hover:from-white/5 hover:to-transparent transition-all"
              />
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="absolute right-0 top-20 bottom-20 w-1/3 cursor-e-resize hover:bg-gradient-to-l hover:from-white/5 hover:to-transparent transition-all"
              />
            </>
          )}

          {/* Indicateurs élégants en lightbox */}
          {photos.length > 1 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3 bg-white/10 backdrop-blur-md px-5 py-3 rounded-full">
              {photos.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(index);
                  }}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentIndex
                      ? 'bg-white w-10 h-2.5'
                      : 'bg-white/40 hover:bg-white/70 w-2.5 h-2.5'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PromotionGallery;
